<template>
  <DefaultLayout :disableFacilitatorTools="true">
    <div>
      <div class="text-center">
        <h4>{{ $t("title") }}</h4>
      </div>

      <br />
      {{ $t("enteremail") }}
      <br />
      <br />

      <form role="form" @submit.prevent="passes(submit)">
        <small class="text-uppercase font-weight-bold">{{ $t("password") }}</small>
        <base-input
          maxLength="50"
          type="password"
          id="input-password1"
          class="mb-3"
          v-model="form.password1"
          v-bind:placeholder="$t('password')"
        />

        <small class="text-uppercase font-weight-bold">{{ $t("password-again") }}</small>
        <base-input
          maxLength="50"
          type="password"
          id="input-password2"
          class="mb-3"
          v-model="form.password2"
          v-bind:placeholder="$t('password-again')"
        />
      </form>

      <div class="alert alert-primary" role="alert" v-if="alertMessage">
        {{ $t(alertMessage) }}
      </div>

      <div class="text-center" v-if="!passwordChanged">
        <base-button type="primary" v-on:click="onSubmit()">{{ $t("submit") }}</base-button>

        <router-link to="/">
          <small>{{ $t("cancel") }}</small>
        </router-link>
      </div>
      <div class="text-center" v-else>
        <router-link to="/admin/login">
          {{ $t("back") }}
        </router-link>
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import { FadeTransition } from "vue2-transitions";
import { changePassword } from "@/services/APIService";
import DefaultLayout from "@/layouts/DefaultLayout.vue";

export default {
  components: { DefaultLayout },
  data() {
    return {
      form: {
        password1: "",
        password2: ""
      },
      alertMessage: "",
      email: "",
      token: "",
      passwordChanged: false
    };
  },
  mounted() {
    this.email = this.$route.params.email;
    this.token = this.$route.params.token;
  },
  methods: {
    onSubmit() {
      if (!this.form.password1 || !this.form.password2) {
        this.alertMessage = "allfieldsrequired";
        return;
      }

      if (this.form.password1 !== this.form.password2) {
        this.alertMessage = "passwordsdontmatch";
        return;
      }

      changePassword(this.email, this.token, this.form.password1)
        .then(response => {
          this.alertMessage = "changed";
          this.passwordChanged = true;
        })
        .catch(error => {
          if (error.response.status === 417) {
            // Too easy a password
            this.alertMessage = "tooeasypassword";
          } else {
            this.alertMessage = "tryagainlater";
          }
        });
    }
  }
};
</script>

<i18n>
{
  "en": {
    "password": "* New password",
    "password-again": "* Type the password again",
    "enteremail": "Fill in your new password and click submit to change your password.",
    "title": "Set password",
    "submit": "Submit",
    "allfieldsrequired": "Please fill in all fields before proceeding.",
    "sendfailed": "Failed to send password reset email. Please try again later.",
    "cancel": "Cancel",
    "passwordsdontmatch": "Passwords do not match.",
    "tooeasypassword": "Password is too weak. Please choose a more complex password and try again.",
    "tryagainlater": "Failed to change password. Please try again later.",
    "back": "Go to login.",
    "changed": "Password changed."
  },
  "fi": {
    "password": "* Uusi salasana",
    "password-again": "* Kirjoita uusi salasanasi toisen kerran",
    "enteremail": "Syötä uusi salasana ja klikkaa Lähetä asettaaksesi salasanasi.",
    "title": "Aseta salasana",
    "submit": "Lähetä",
    "allfieldsrequired": "Täytäthän kaikki kentät ennen jatkamista.",
    "sendfailed": "Sähköpostin lähetys epäonnistui. Yritä uudelleen myöhemmin.",
    "cancel": "Peru",
    "passwordsdontmatch": "Salasanat eivät täsmää.",
    "tooeasypassword": "Valitsit liian helposti murrettavan salasanan. Yritä uudelleen monimutkaisemmalla salasanalla.",
    "tryagainlater": "Salasanan vaihtaminen epäonnistui tuntemattomasta syystä. Yritä uudelleen myöhemmin.",
    "back": "Siirry sisäänkirjautumiseen.",
    "changed": "Salasana vaihdettu."
  },
  "sv": {
    "password": "* Nytt lösenord",
    "password-again": "* Ange det nya lösenordet igen",
    "enteremail": "Ange det nya lösenordet och klicka på Skicka för att ändra ditt lösenord.",
    "title": "Byt lösenord",
    "submit": "Skicka",
    "allfieldsrequired": "Vänligen fyll i alla fält innan du fortsätter.",
    "sendfailed": "E-postmeddelandet skickades inte. Försök igen senare.",
    "cancel": "Ångra",
    "passwordsdontmatch": "Lösenorden stämmer inte överens.",
    "tooeasypassword": "Du valde ett lösenord som är för lätt att knäcka. Försök igen med ett mer komplext lösenord.",
    "tryagainlater": "Ändring av lösenordet misslyckades av okänd anledning. Försök igen senare.",
    "back": "Återgå till inloggning.",
    "changed": "Lösenordet har ändrats."
  },
  "de": {
    "password": "* Neues Passwort",
    "password-again": "* Geben Sie das Passwort erneut ein",
    "enteremail": "Geben Sie Ihr neues Passwort ein und klicken Sie auf Senden, um Ihr Passwort zu ändern.",
    "title": "Passwort ändern",
    "submit": "Senden",
    "allfieldsrequired": "Bitte füllen Sie alle Felder aus, bevor Sie fortfahren.",
    "sendfailed": "E-Mail zum Zurücksetzen des Passworts konnte nicht gesendet werden. Bitte versuchen Sie es später noch einmal.",
    "cancel": "Abbrechen",
    "passwordsdontmatch": "Passwörter stimmen nicht überein.",
    "tooeasypassword": "Passwort ist zu schwach. Bitte wählen Sie ein komplizierteres Passwort aus und versuchen Sie es erneut.",
    "tryagainlater": "Passwort konnte nicht geändert werden. Bitte versuchen Sie es später noch einmal.",
    "back": "Gehen Sie zur Anmelden.",
    "changed": " Passwort geändert."
  },
  "ko": {
    "password": "* 새 비밀번호",
    "password-again": "* 비밀번호를 다시 입력해주십시오.",
    "enteremail": "비밀번호 변경을 위해 새 비밀번호를 입력 후 제출 버튼을 클릭해주십시오. ",
    "title": "비밀번호 설정",
    "submit": "제출",
    "allfieldsrequired": "모든 정보를 입력해주십시오.",
    "sendfailed": "비밀번호 재설정 이메일 발송을 실패하였습니다. 잠시 후 다시 시도해주십시오.",
    "cancel": "취소",
    "passwordsdontmatch": "비밀번호가 일치하지 않습니다.",
    "tooeasypassword": "비밀번호가 보안에 취약합니다. 다른 비밀번호로 다시 시도해주십시오.",
    "tryagainlater": "비밀번호 변경에 실패하였습니다. 잠시 후 다시 시도해주십시오.",
    "back": "로그인화면으로 가기",
    "changed": "비밀번호가 변경되었습니다."
  }
}
</i18n>
